import { useState, useRef, useCallback, useEffect, memo } from 'react';
import { Play, Pause, Loader2, AlertCircle } from 'lucide-react';

interface SimpleAudioPlayerProps {
  src: string;
  compact?: boolean;
}

const PLAYBACK_SPEEDS = [1, 1.5, 2] as const;
type PlaybackSpeed = (typeof PLAYBACK_SPEEDS)[number];

export const SimpleAudioPlayer = memo(function SimpleAudioPlayer({
  src,
  compact = false,
}: SimpleAudioPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState<PlaybackSpeed>(1);
  const audioRef = useRef<HTMLAudioElement>(null);
  const animationRef = useRef<number | null>(null);

  const formatTime = (seconds: number) => {
    if (!isFinite(seconds) || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const updateProgress = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    setProgress((audio.currentTime / duration) * 100);
    if (!audio.paused && !audio.ended) {
      animationRef.current = requestAnimationFrame(updateProgress);
    }
  }, [duration]);

  useEffect(() => {
    if (isPlaying) {
      animationRef.current = requestAnimationFrame(updateProgress);
    } else if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isPlaying, updateProgress]);

  // Reset on src change
  useEffect(() => {
    setLoading(true);
    setError(false);
    setProgress(0);
    setDuration(0);
    setIsPlaying(false);
    setPlaybackSpeed(1);
  }, [src]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || error || loading) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(() => setError(true));
    }
  }, [isPlaying, error, loading]);

  const cycleSpeed = useCallback(() => {
    const idx = PLAYBACK_SPEEDS.indexOf(playbackSpeed);
    const newSpeed = PLAYBACK_SPEEDS[(idx + 1) % PLAYBACK_SPEEDS.length];
    setPlaybackSpeed(newSpeed);
    if (audioRef.current) audioRef.current.playbackRate = newSpeed;
  }, [playbackSpeed]);

  const handleProgressClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const audio = audioRef.current;
      if (!audio || !duration) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const pos = (e.clientX - rect.left) / rect.width;
      audio.currentTime = pos * duration;
      setProgress(pos * 100);
    },
    [duration]
  );

  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      setLoading(false);
    }
  }, []);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    setProgress(0);
    setPlaybackSpeed(1);
    if (audioRef.current) audioRef.current.playbackRate = 1;
  }, []);

  const handleCanPlayThrough = useCallback(() => {
    setLoading(false);
  }, []);

  const handleError = useCallback(() => {
    setError(true);
    setLoading(false);
  }, []);

  const currentTime = (progress / 100) * duration;

  if (error) {
    return (
      <div className={`flex items-center gap-2 ${compact ? 'min-w-[160px]' : 'min-w-[220px]'}`}>
        <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
        <span className="text-xs text-red-500">Audio non disponibile</span>
      </div>
    );
  }

  return (
    <div
      className={`flex items-center gap-2 bg-[rgba(55,53,47,0.04)] dark:bg-[#2F2F2F] rounded-lg ${
        compact ? 'px-2.5 py-1.5 min-w-[180px]' : 'px-3 py-2 min-w-[240px]'
      }`}
    >
      {/* Play/Pause */}
      <button
        onClick={togglePlay}
        disabled={loading}
        className={`flex-shrink-0 flex items-center justify-center rounded-full transition-colors ${
          compact ? 'w-7 h-7' : 'w-8 h-8'
        } ${
          loading
            ? 'bg-[rgba(55,53,47,0.08)] dark:bg-white/10'
            : 'bg-[#2383E2] dark:bg-[#529CCA] hover:bg-[#1D6FBF] dark:hover:bg-[#4A8CB8]'
        }`}
      >
        {loading ? (
          <Loader2 className={`${compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} text-[#787774] animate-spin`} />
        ) : isPlaying ? (
          <Pause className={`${compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} text-white`} />
        ) : (
          <Play className={`${compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} text-white ml-0.5`} />
        )}
      </button>

      {/* Progress + Time */}
      <div className="flex-1 flex flex-col gap-0.5">
        <div
          className="h-1 bg-[rgba(55,53,47,0.12)] dark:bg-white/15 rounded-full cursor-pointer"
          onClick={handleProgressClick}
        >
          <div
            className="h-full bg-[#2383E2] dark:bg-[#529CCA] rounded-full transition-[width] duration-75"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex justify-between">
          <span className={`text-[#787774] dark:text-[#999999] ${compact ? 'text-[9px]' : 'text-[10px]'}`}>
            {isPlaying || progress > 0 ? formatTime(currentTime) : formatTime(duration)}
          </span>
        </div>
      </div>

      {/* Speed button (only when playing) */}
      {isPlaying && (
        <button
          onClick={cycleSpeed}
          className={`flex-shrink-0 rounded-md bg-[rgba(55,53,47,0.08)] dark:bg-white/10 hover:bg-[rgba(55,53,47,0.16)] dark:hover:bg-white/20 transition-colors flex items-center justify-center ${
            compact ? 'h-5 px-1.5' : 'h-6 px-2'
          }`}
        >
          <span className={`font-bold text-[#37352F] dark:text-white ${compact ? 'text-[9px]' : 'text-[10px]'}`}>
            {playbackSpeed}x
          </span>
        </button>
      )}

      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onLoadedMetadata={handleLoadedMetadata}
        onCanPlayThrough={handleCanPlayThrough}
        onEnded={handleEnded}
        onError={handleError}
      />
    </div>
  );
});
